import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Menu, X, ArrowRight, ChevronRight, Check,
  Building2, Sun, Route, Droplets, Zap, Package,
  ClipboardList, Truck, Calculator, LayoutDashboard, ClipboardCheck, Users,
  IndianRupee,
  FileText, Star, Quote, Mail, Phone, MapPin,
  TrendingUp, Shield, Play,
} from "lucide-react";

/* ─── VIDEO ID — paste your YouTube/Loom ID here to activate ──── */
const WALKTHROUGH_VIDEO_ID = ""; // e.g. "dQw4w9WgXcQ" or your Loom embed ID

/* ─── FONTS ───────────────────────────────────────────────────────
   One family for the whole site: Inter
   Weights used: 400 body · 500 nav/labels · 600 buttons · 700 headings
──────────────────────────────────────────────────────────────── */
const INTER = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
const F = {
  display: INTER,
  body:    INTER,
  mono:    INTER,
  label:   INTER,
};

/* ─── DESIGN TOKENS (LIGHT THEME) ─────────────────────────────── */
const T = {
  bg:      "#F7F9FC",
  bg2:     "#EBF0F8",
  bg3:     "#E1E8F3",
  card:    "#FFFFFF",
  gold:    "#A8700C",
  gold2:   "#85580A",
  blue:    "#2A52BE",
  blue2:   "#1B3A8E",
  teal:    "#0891B2",
  sky:     "#0284C7",
  em:      "#059669",
  text:    "#0F172A",
  muted:   "#475569",
  dim:     "#64748B",
  border:  "rgba(15,23,42,0.10)",
  borderB: "rgba(42,82,190,0.25)",
};

/* Dark palette — used only for the "product screens" (dashboard mockup + video modal)
   so they stay dark against the light page. Set these equal to T to make them light too. */
const D = {
  bg: "#040C18", gold: "#E8B832", gold2: "#C8961E", blue: "#2A52BE", blue2: "#1B3A8E",
  teal: "#00E5FF", sky: "#38BDF8", em: "#10B981",
  text: "#F8FAFC", muted: "#94A3B8", dim: "#64748B",
};

/* true on phones — used to soften 3D angles */
function useIsMobile(bp = 640) {
  const [m, setM] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${bp}px)`);
    const h = () => setM(mq.matches);
    h();
    if (mq.addEventListener) mq.addEventListener("change", h); else mq.addListener(h);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", h); else mq.removeListener(h); };
  }, [bp]);
  return m;
}

/* depth helper for children inside a Tilt3D card */
const Z = (n) => ({ transform: `translateZ(${n}px)` });

/* card surface: soft white → pale-blue diagonal gradient */
const CARD_BG = "linear-gradient(160deg, #FFFFFF 0%, #F2F6FC 100%)";

/* scroll-reveal: any element with className "reveal" fades/slides in once visible */
function useReveal() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(".reveal"));
    if (!("IntersectionObserver" in window)) { els.forEach(e => e.classList.add("in")); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: "0px 0px -6% 0px" });
    els.forEach(e => io.observe(e));
    return () => io.disconnect();
  }, []);
}

/* ─── INJECTED STYLES ─────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

  @keyframes float-y {
    0%   { transform: translateY(0px); }
    100% { transform: translateY(-16px); }
  }
  @keyframes chip-float {
    0%   { transform: translateZ(70px) translateY(0px); }
    100% { transform: translateZ(70px) translateY(-10px); }
  }
  @keyframes orb-pulse {
    0%, 100% { opacity: 0.35; transform: scale(1); }
    50%      { opacity: 0.65; transform: scale(1.08); }
  }
  @keyframes shimmer-text {
    0%   { background-position: -400% center; }
    100% { background-position: 400% center; }
  }
  @keyframes slide-up {
    from { opacity: 0; transform: translateY(32px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes bounce-x {
    0%,100% { transform: translateX(0); }
    50%     { transform: translateX(6px); }
  }
  @keyframes cube-spin {
    from { transform: rotateX(-22deg) rotateY(0deg); }
    to   { transform: rotateX(-22deg) rotateY(360deg); }
  }
  @keyframes grid-scroll {
    from { background-position: 0 0; }
    to   { background-position: 0 64px; }
  }
  @keyframes modal-backdrop-in  { from { opacity: 0; } to { opacity: 1; } }
  @keyframes modal-backdrop-out { from { opacity: 1; } to { opacity: 0; } }
  @keyframes modal-card-in {
    from { opacity: 0; transform: perspective(1200px) rotateX(14deg) scale(0.92) translateY(30px); }
    to   { opacity: 1; transform: perspective(1200px) rotateX(0deg)  scale(1)    translateY(0px); }
  }
  @keyframes modal-card-out {
    from { opacity: 1; transform: perspective(1200px) rotateX(0deg)  scale(1)    translateY(0px); }
    to   { opacity: 0; transform: perspective(1200px) rotateX(-10deg) scale(0.93) translateY(20px); }
  }
  .modal-backdrop-enter { animation: modal-backdrop-in 0.28s ease forwards; }
  .modal-backdrop-exit  { animation: modal-backdrop-out 0.22s ease forwards; }
  .modal-card-enter     { animation: modal-card-in  0.4s cubic-bezier(0.34,1.56,0.64,1) forwards; }
  .modal-card-exit      { animation: modal-card-out 0.22s ease forwards; }

  .play-ring { transition: transform 0.25s ease, box-shadow 0.25s ease; }
  .play-ring:hover {
    transform: scale(1.08);
    box-shadow: 0 0 0 8px rgba(232,184,50,0.15), 0 0 40px rgba(232,184,50,0.25);
  }

  .hero-float { animation: float-y 6s ease-in-out infinite alternate; }
  .orb        { animation: orb-pulse 5s ease-in-out infinite; }
  .orb2       { animation: orb-pulse 7s ease-in-out infinite reverse; }
  .slide-up   { animation: slide-up 0.7s ease-out forwards; }
  .bounce-x   { animation: bounce-x 1.8s ease-in-out infinite; }
  .hero-chip  { animation: chip-float 4s ease-in-out infinite alternate; }

  .shimmer-txt {
    background: linear-gradient(90deg, #0F172A 0%, #A8700C 30%, #85580A 50%, #A8700C 70%, #0F172A 100%);
    background-size: 400% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: shimmer-text 5s linear infinite;
  }

  /* ── 3D floor grid (hero) ── */
  .floor-grid {
    position: absolute; left: -20%; right: -20%; bottom: -8%; height: 55%;
    background-image:
      linear-gradient(rgba(168,112,12,0.16) 1px, transparent 1px),
      linear-gradient(90deg, rgba(42,82,190,0.14) 1px, transparent 1px);
    background-size: 64px 64px;
    transform: perspective(500px) rotateX(64deg);
    transform-origin: center bottom;
    animation: grid-scroll 3s linear infinite;
    mask-image: linear-gradient(to top, black 10%, transparent 90%);
    -webkit-mask-image: linear-gradient(to top, black 10%, transparent 90%);
  }

  /* ── 3D tilt card ── */
  .tilt { box-shadow: 0 1px 2px rgba(15,23,42,0.05), 0 12px 32px rgba(15,23,42,0.07); transition: box-shadow 0.3s ease, border-color 0.3s ease; }
  .tilt:hover {
    box-shadow: 0 28px 56px rgba(15,23,42,0.14), 0 0 30px rgba(168,112,12,0.10);
    border-color: rgba(168,112,12,0.45) !important;
  }
  .tilt-glare {
    position: absolute; inset: 0; border-radius: inherit; pointer-events: none;
    opacity: 0; transition: opacity 0.3s ease;
    background: radial-gradient(circle at var(--mx, 50%) var(--my, 50%), rgba(168,112,12,0.08), transparent 55%);
    transform: translateZ(1px);
  }
  .tilt:hover .tilt-glare { opacity: 1; }

  /* ── 3D extruded buttons ── */
  .btn-gold-hover {
    box-shadow: 0 5px 0 #6B4807, 0 12px 24px rgba(168,112,12,0.25);
    transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.2s ease;
  }
  .btn-gold-hover:hover  { transform: translateY(-2px); box-shadow: 0 7px 0 #6B4807, 0 18px 32px rgba(168,112,12,0.35); filter: brightness(1.06); }
  .btn-gold-hover:active { transform: translateY(4px);  box-shadow: 0 1px 0 #6B4807, 0 4px 10px rgba(168,112,12,0.3); }

  .btn-glass-hover {
    box-shadow: 0 3px 0 rgba(15,23,42,0.08), 0 8px 18px rgba(15,23,42,0.08);
    transition: transform 0.15s ease, background 0.2s ease, border-color 0.2s ease, box-shadow 0.15s ease;
  }
  .btn-glass-hover:hover  { transform: translateY(-2px); background: rgba(168,112,12,0.08) !important; border-color: rgba(168,112,12,0.5) !important; }
  .btn-glass-hover:active { transform: translateY(3px);  box-shadow: 0 1px 0 rgba(15,23,42,0.08); }

  .nav-link { transition: color 0.2s; }
  .nav-link:hover { color: #E8B832 !important; }

  .sector-tab-active {
    background: linear-gradient(135deg, rgba(42,82,190,0.12), rgba(168,112,12,0.18)) !important;
    border-color: rgba(168,112,12,0.5) !important;
    color: #85580A !important;
    transform: translateY(-3px);
    box-shadow: 0 5px 0 rgba(168,112,12,0.35), 0 12px 22px rgba(15,23,42,0.12) !important;
  }

  /* ── CSS 3D cube ── */
  .cube { position: relative; width: 64px; height: 64px; transform-style: preserve-3d; animation: cube-spin 14s linear infinite; }
  .cube-face {
    position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
    border: 1px solid rgba(232,184,50,0.55);
    background: linear-gradient(135deg, rgba(27,58,142,0.55), rgba(200,150,30,0.25));
    backdrop-filter: none;
  }

  /* ══ RESPONSIVE ══ */
  .nav-burger, .nav-mobile-panel { display: none; }

  @media (max-width: 900px) {
    .nav-desktop { display: none !important; }
    .nav-burger { display: flex !important; align-items: center; }
    .nav-mobile-panel { display: block !important; }

    .hero-grid { grid-template-columns: 1fr !important; gap: 24px !important; min-height: auto !important; padding: 56px 24px 72px !important; }
    .features-grid { grid-template-columns: repeat(2, 1fr) !important; }
    .sector-card { grid-template-columns: 1fr !important; gap: 0 !important; }
    .steps-grid, .pricing-grid, .testi-grid { grid-template-columns: 1fr !important; }
    .steps-line { display: none !important; }
    .pricing-card { margin-top: 0 !important; margin-bottom: 0 !important; }
    .contact-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
    .footer-grid { grid-template-columns: repeat(3, 1fr) !important; gap: 32px !important; }
    .footer-grid > div:first-child { grid-column: 1 / -1; }
  }

  @media (max-width: 640px) {
    .sec { padding: 64px 20px !important; }
    .hero-grid { padding: 40px 20px 64px !important; }
    .features-grid { grid-template-columns: 1fr !important; }
    .feature-lg { grid-column: span 1 !important; }
    .sector-pad { padding: 28px 22px !important; }
    .sector-img-wrap { padding: 0 16px 16px !important; }
    .sector-img-wrap > div { min-height: 200px !important; }
    .form-pad { padding: 24px !important; }
    .form-pad > div:nth-child(-n+2) { grid-template-columns: 1fr !important; }
    .footer-grid { grid-template-columns: 1fr 1fr !important; }
    .footer-root { padding-left: 20px !important; padding-right: 20px !important; }
    .hero-cube { display: none !important; }
    .hero-chip-a { right: 0 !important; }
    .hero-chip-b { left: 0 !important; }
    .hero-chip-c { right: 10px !important; }
    .mock-sidebar { display: none !important; }
    .mock-stats { grid-template-columns: repeat(2, 1fr) !important; }
  }

  /* ── blurred hero background (change --hero-blur to taste) ── */
  .hero-bg-blur {
    --hero-blur: 4px;
    position: absolute; inset: -28px;
    filter: blur(var(--hero-blur));
    will-change: filter;
  }
  @media (max-width: 640px) { .hero-bg-blur { --hero-blur: 2.5px; } }

  /* ══ POLISH ══ */
  html { scroll-behavior: smooth; scroll-padding-top: 84px; }
  ::selection { background: rgba(232,184,50,0.35); color: #fff; }
  :focus-visible { outline: 2px solid #38BDF8; outline-offset: 3px; border-radius: 6px; }
  * { scrollbar-width: thin; scrollbar-color: rgba(168,112,12,0.45) #EBF0F8; }
  ::-webkit-scrollbar { width: 10px; height: 10px; }
  ::-webkit-scrollbar-track { background: #EBF0F8; }
  ::-webkit-scrollbar-thumb { background: rgba(232,184,50,0.3); border-radius: 8px; border: 2px solid #EBF0F8; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(232,184,50,0.5); }

  /* fine film-grain over the whole page — removes gradient banding */
  body::after {
    content: ""; position: fixed; inset: 0; pointer-events: none; z-index: 9999; opacity: 0.045;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  }

  /* gradient gold text for section headings */
  .gold-grad {
    background: linear-gradient(110deg, #C9921A 0%, #A8700C 50%, #7A520A 100%);
    -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
  }

  /* scroll reveal (uses the translate property so it composes with 3D transforms) */
  .reveal { opacity: 0; translate: 0 30px; transition: opacity 0.9s ease, translate 0.9s cubic-bezier(0.2,0.8,0.2,1); transition-delay: var(--d, 0ms); }
  .reveal.in { opacity: 1; translate: 0 0; }
  .reveal.tilt { transition: opacity 0.9s ease, translate 0.9s cubic-bezier(0.2,0.8,0.2,1), box-shadow 0.3s ease, border-color 0.3s ease; transition-delay: var(--d, 0ms), var(--d, 0ms), 0s, 0s; }

  /* soft coloured glow behind each section */
  .sec { position: relative; isolation: isolate; }
  .sec::before { content: ""; position: absolute; inset: 0; z-index: -1; pointer-events: none; }
  .glow-a::before { background: radial-gradient(ellipse 60% 50% at 88% 0%, rgba(232,184,50,0.10), transparent 70%), radial-gradient(ellipse 50% 45% at 0% 100%, rgba(42,82,190,0.20), transparent 70%); }
  .glow-b::before { background: radial-gradient(ellipse 55% 45% at 8% 8%, rgba(56,189,248,0.09), transparent 70%), radial-gradient(ellipse 50% 45% at 96% 92%, rgba(232,184,50,0.09), transparent 70%); }
  .glow-c::before { background: radial-gradient(ellipse 65% 50% at 50% 0%, rgba(42,82,190,0.24), transparent 70%); }

  /* gold hairline highlight along the top edge of every card */
  .tilt:not(.no-edge)::before {
    content: ""; position: absolute; top: 0; left: 12%; right: 12%; height: 1px; pointer-events: none;
    background: linear-gradient(90deg, transparent, rgba(232,184,50,0.6), transparent);
  }

  /* light sweep across the gold buttons */
  .btn-gold-hover { position: relative; overflow: hidden; }
  .btn-gold-hover::after {
    content: ""; position: absolute; top: 0; left: -70%; width: 45%; height: 100%; pointer-events: none;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.55), transparent);
    transform: skewX(-20deg); transition: left 0.65s ease;
  }
  .btn-gold-hover:hover::after { left: 130%; }

  /* sector ticker */
  @keyframes ticker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
  .ticker-track { display: flex; width: max-content; animation: ticker 40s linear infinite; }
  .ticker:hover .ticker-track { animation-play-state: paused; }

  /* ══ GLOBAL TYPOGRAPHY ══ */
  button, input, select, textarea { font-family: inherit; }
  h1, h2, h3, h4 { font-optical-sizing: auto; }
  body { font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; font-feature-settings: "cv11", "ss01"; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; text-rendering: optimizeLegibility; }

  /* ══ MOBILE REFINEMENTS ══ */
  html { -webkit-text-size-adjust: 100%; }
  img { max-width: 100%; }
  button, a { -webkit-tap-highlight-color: transparent; }

  @media (hover: none) {
    .tilt:hover { box-shadow: none; }
    .tilt-glare { display: none; }
    .btn-gold-hover:hover, .btn-glass-hover:hover { transform: none; }
  }

  @media (max-width: 640px) {
    .hero-cta { flex-direction: column; }
    .hero-cta > button { width: 100%; justify-content: center; }
    .hero-stats { gap: 20px 16px !important; }
    .hero-stats > div { flex: 1 1 40%; }

    .sector-tabs { flex-wrap: nowrap !important; overflow-x: auto; padding-bottom: 14px; margin-left: -20px; margin-right: -20px; padding-left: 20px; padding-right: 20px; scrollbar-width: none; }
    .sector-tabs::-webkit-scrollbar { display: none; }
    .sector-tabs > button { flex-shrink: 0; white-space: nowrap; }

    .form-pad input, .form-pad select, .form-pad textarea { font-size: 16px !important; }

    [data-testid="video-modal-backdrop"] { padding: 12px !important; }
    [data-testid="video-modal-card"] > div:first-child,
    [data-testid="video-modal-card"] > div:last-child { padding: 12px 14px !important; }

    .footer-root > div > div:last-child { justify-content: center !important; text-align: center; }
  }

  @media (max-width: 380px) {
    .hero-grid { padding-left: 16px !important; padding-right: 16px !important; }
    .sec { padding-left: 16px !important; padding-right: 16px !important; }
    .footer-grid { grid-template-columns: 1fr !important; }
  }

  @media (prefers-reduced-motion: reduce) {
    .hero-float, .hero-chip, .cube, .floor-grid, .orb, .orb2, .bounce-x, .shimmer-txt, .ticker-track { animation: none !important; }
    .reveal { opacity: 1 !important; translate: none !important; transition: none !important; }
    .tilt { transform: none !important; }
  }
`;

/* ─── TILT 3D WRAPPER ─────────────────────────────────────────────
   Wrap any card. It follows the mouse in 3D and children can pop out
   using style={Z(40)} (translateZ).
   NOTE: don't put backdrop-filter / overflow:hidden on the same
   element as its 3D children — the browser flattens them.
──────────────────────────────────────────────────────────────── */
function Tilt3D({ children, max = 8, base = [0, 0], glare = true, style, className = "", ...rest }) {
  const ref = useRef(null);
  const rest3d = (rx, ry) =>
    `perspective(1100px) rotateX(${base[0] + rx}deg) rotateY(${base[1] + ry}deg)`;

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    el.style.transition = "transform 0.08s linear, box-shadow 0.3s ease, border-color 0.3s ease";
    el.style.transform = rest3d((0.5 - py) * max * 2, (px - 0.5) * max * 2);
    el.style.setProperty("--mx", `${px * 100}%`);
    el.style.setProperty("--my", `${py * 100}%`);
  };
  const onLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.transition = "transform 0.7s cubic-bezier(0.2,0.8,0.2,1), box-shadow 0.3s ease, border-color 0.3s ease";
    el.style.transform = rest3d(0, 0);
  };

  return (
    <div
      ref={ref}
      className={`tilt ${className}`}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{
        position: "relative",
        transformStyle: "preserve-3d",
        transform: rest3d(0, 0),
        willChange: "transform",
        ...style,
      }}
      {...rest}
    >
      {children}
      {glare && <div className="tilt-glare" />}
    </div>
  );
}

/* ─── VIDEO MODAL (stays dark — uses D palette) ───────────────── */
function VideoModal({ onClose }) {
  const [exiting, setExiting] = useState(false);
  const [src, setSrc] = useState("");
  const backdropRef = useRef(null);

  useEffect(() => {
    if (!WALKTHROUGH_VIDEO_ID || WALKTHROUGH_VIDEO_ID.startsWith("//")) return;
    const t = setTimeout(() => {
      setSrc(`https://www.youtube.com/embed/${WALKTHROUGH_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1&color=white`);
    }, 280);
    return () => clearTimeout(t);
  }, []);

  const handleClose = useCallback(() => {
    setSrc("");
    setExiting(true);
    setTimeout(onClose, 220);
  }, [onClose]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const onBackdropClick = (e) => {
    if (e.target === backdropRef.current) handleClose();
  };

  return (
    <div
      ref={backdropRef}
      data-testid="video-modal-backdrop"
      onClick={onBackdropClick}
      className={exiting ? "modal-backdrop-exit" : "modal-backdrop-enter"}
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(2,7,16,0.88)",
        backdropFilter: "blur(14px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        data-testid="video-modal-card"
        className={exiting ? "modal-card-exit" : "modal-card-enter"}
        style={{
          width: "100%", maxWidth: 900,
          background: "rgba(8,19,38,0.98)",
          border: "1px solid rgba(232,184,50,0.2)",
          borderRadius: 20,
          overflow: "hidden",
          boxShadow: "0 60px 120px rgba(4,12,24,0.9), 0 0 60px rgba(27,58,142,0.25)",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 24px",
          borderBottom: "1px solid rgba(232,184,50,0.1)",
          background: "rgba(4,12,24,0.6)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: "linear-gradient(135deg, rgba(27,58,142,0.6), rgba(200,150,30,0.2))",
              border: "1px solid rgba(232,184,50,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Play size={14} color={D.gold} fill={D.gold} />
            </div>
            <div>
              <div style={{ fontFamily: F.display, fontWeight: 700, fontSize: 15, color: D.text }}>
                Sitera — 2-Minute Walkthrough
              </div>
              <div style={{ fontFamily: F.mono, fontSize: 10, color: D.dim, marginTop: 1, letterSpacing: "0.1em" }}>
                CONSTRUCTION ERP · FULL DEMO
              </div>
            </div>
          </div>
          <button
            data-testid="video-modal-close-btn"
            onClick={handleClose}
            style={{
              width: 34, height: 34, borderRadius: 8,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: D.muted, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background 0.2s, color 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(232,184,50,0.1)"; e.currentTarget.style.color = D.gold; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.color = D.muted; }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ position: "relative", paddingBottom: "56.25%", background: "#020710" }}>
          {src ? (
            <iframe
              data-testid="video-modal-iframe"
              src={src}
              title="Sitera 2-Minute Walkthrough"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
            />
          ) : (
            <div style={{
              position: "absolute", inset: 0,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              gap: 20,
              background: "linear-gradient(135deg, #04091A 0%, #081326 50%, #04091A 100%)",
            }}>
              <div style={{
                position: "absolute", inset: 0,
                backgroundImage: "linear-gradient(rgba(27,58,142,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(27,58,142,0.06) 1px, transparent 1px)",
                backgroundSize: "40px 40px",
              }} />
              <div style={{
                position: "absolute", top: "50%", left: "50%",
                width: 300, height: 200,
                background: "radial-gradient(ellipse, rgba(27,58,142,0.2) 0%, transparent 70%)",
                transform: "translate(-50%,-50%)", filter: "blur(30px)",
              }} />
              <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                <div style={{
                  width: 72, height: 72, borderRadius: "50%",
                  background: "linear-gradient(135deg, rgba(27,58,142,0.6), rgba(200,150,30,0.2))",
                  border: "1.5px solid rgba(232,184,50,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 0 40px rgba(232,184,50,0.15)",
                }}>
                  <Play size={28} color={D.gold} fill={D.gold} style={{ marginLeft: 4 }} />
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: F.display, fontSize: 16, fontWeight: 700, color: D.text, marginBottom: 6 }}>
                    Walkthrough recording in progress
                  </div>
                  <div style={{ fontFamily: F.mono, fontSize: 11, color: D.dim, letterSpacing: "0.1em" }}>
                    Add your YouTube or Loom ID to <span style={{ color: D.gold }}>WALKTHROUGH_VIDEO_ID</span>
                  </div>
                </div>
              </div>
              <div style={{ position: "absolute", bottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: D.gold, animation: "orb-pulse 1.2s ease-in-out infinite" }} />
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: D.gold, animation: "orb-pulse 1.2s ease-in-out infinite 0.4s" }} />
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: D.gold, animation: "orb-pulse 1.2s ease-in-out infinite 0.8s" }} />
              </div>
            </div>
          )}
        </div>

        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 24px",
          borderTop: "1px solid rgba(232,184,50,0.08)",
          background: "rgba(4,12,24,0.5)",
          flexWrap: "wrap", gap: 10,
        }}>
          <span style={{ fontSize: 12, color: D.dim }}>
            See how Sitera manages 6 construction sectors end-to-end
          </span>
          <button
            data-testid="video-modal-request-demo-btn"
            onClick={() => { handleClose(); setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 300); }}
            className="btn-gold-hover"
            style={{
              background: `linear-gradient(135deg, ${D.gold2}, ${D.gold})`,
              color: "#040C18", border: "none",
              padding: "8px 18px", borderRadius: 8,
              fontSize: 13, fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            Request Demo <ArrowRight size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── HEADER ──────────────────────────────────────────────────── */
function Header() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const links = ["Features", "Sectors", "Pricing", "Contact"];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="nav-header" style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      background: scrolled ? "rgba(4,12,24,0.97)" : "rgba(4,12,24,0.88)",
      backdropFilter: "blur(24px)",
      borderBottom: "1px solid rgba(255,255,255,0.08)",
      boxShadow: scrolled ? "0 10px 30px rgba(4,12,24,0.35)" : "none",
      transition: "background 0.3s ease, box-shadow 0.3s ease",
    }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 28px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 68 }}>
          <a data-testid="sitera-brand-logo" href="#top" onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <img src="/brand/sitera-logo.png" alt="Sitera" style={{ height: 38, width: 38, objectFit: "contain", filter: "drop-shadow(0 4px 6px rgba(232,184,50,0.35))" }} />
            <span style={{ fontFamily: F.display, fontWeight: 700, fontSize: 23, color: D.text, letterSpacing: "-0.5px" }}>
              SITE<span style={{ color: D.gold }}>RA</span>
            </span>
          </a>

          <nav style={{ display: "flex", gap: 36 }} className="nav-desktop">
            {links.map(l => (
              <a key={l} href={`#${l.toLowerCase()}`} className="nav-link"
                style={{ color: D.muted, fontSize: 14, fontWeight: 500, textDecoration: "none" }}>{l}</a>
            ))}
          </nav>

          <div style={{ display: "flex", gap: 12 }} className="nav-desktop">
            <button data-testid="nav-login-button" onClick={() => navigate("/login")} className="btn-glass-hover"
              style={{ background: "transparent", border: `1px solid rgba(255,255,255,0.22)`, color: D.text, padding: "9px 20px", borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: "pointer" }}>
              Sign In
            </button>
            <button data-testid="nav-request-demo-button"
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-gold-hover"
              style={{
                background: `linear-gradient(135deg, ${D.gold2}, ${D.gold})`,
                color: "#040C18", padding: "9px 20px", borderRadius: 8,
                fontSize: 14, fontWeight: 700, cursor: "pointer", border: "none",
              }}>
              Request Demo
            </button>
          </div>

          <button className="nav-burger" aria-label="Toggle menu" onClick={() => setOpen(!open)}
            style={{ background: "none", border: "none", color: D.text, cursor: "pointer" }}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", padding: "16px 0 20px" }} className="nav-mobile-panel">
            {links.map(l => (
              <a key={l} href={`#${l.toLowerCase()}`} onClick={() => setOpen(false)}
                style={{ display: "block", color: D.muted, padding: "13px 0", fontSize: 16, textDecoration: "none", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>{l}</a>
            ))}
            <button onClick={() => { setOpen(false); document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }); }}
              style={{ marginTop: 16, width: "100%", background: "transparent", border: `1px solid rgba(255,255,255,0.22)`, color: D.text, padding: 12, borderRadius: 8, fontWeight: 600, fontSize: 15, cursor: "pointer" }}>
              Request Demo
            </button>
            <button onClick={() => navigate("/login")}
              style={{ marginTop: 12, width: "100%", background: `linear-gradient(135deg,${D.gold2},${D.gold})`, color: "#040C18", padding: 12, borderRadius: 8, fontWeight: 700, fontSize: 15, border: "none", cursor: "pointer" }}>
              Sign In
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

/* ─── DASHBOARD MOCKUP (stays dark — uses D palette) ──────────── */
function DashboardMockup() {
  const stats = [
    { label: "Active Projects", val: "24", up: "+3" },
    { label: "Capacity (MWp)", val: "185", up: "+12" },
    { label: "Avg Progress", val: "68%", up: "+4%" },
    { label: "Open Issues", val: "7", down: "-2" },
  ];
  const rows = [
    { name: "NH-48 Expressway", status: "On Track", pct: 72, col: D.em },
    { name: "Tech Park Tower B", status: "At Risk", pct: 44, col: D.gold },
    { name: "Rewa Solar Phase 3", status: "On Track", pct: 89, col: D.em },
    { name: "Nashik WTP Expansion", status: "Delayed", pct: 31, col: "#EF4444" },
  ];
  return (
    <div data-testid="hero-3d-dashboard-mockup" style={{
      borderRadius: 16,
      overflow: "hidden",
      width: "100%",
      boxShadow: "0 40px 80px rgba(27,58,142,0.5), 0 0 120px rgba(200,150,30,0.18), inset 0 1px 0 rgba(255,255,255,0.1)",
      border: "1px solid rgba(232,184,50,0.2)",
    }}>
      <div style={{ background: "#0a1628", padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#EF4444" }} />
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: D.gold }} />
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: D.em }} />
        <div style={{ flex: 1, margin: "0 16px", background: "rgba(255,255,255,0.06)", borderRadius: 6, height: 20, display: "flex", alignItems: "center", paddingLeft: 10 }}>
          <span style={{ fontFamily: F.mono, fontSize: 10, color: D.dim }}>app.sitera.in</span>
        </div>
      </div>

      <div style={{ display: "flex", background: "#060F20", minHeight: 340 }}>
        <div className="mock-sidebar" style={{ width: 140, background: "#04091A", borderRight: "1px solid rgba(255,255,255,0.05)", padding: "16px 0" }}>
          <div style={{ padding: "0 12px 12px", marginBottom: 8 }}>
            <img src="/brand/sitera-logo.png" alt="" style={{ height: 22, width: 22, objectFit: "contain" }} />
          </div>
          {["Dashboard", "Field Ops", "Clients", "Finance", "Estimates", "Vendors"].map((item, i) => (
            <div key={i} style={{
              padding: "5px 14px", fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: i === 0 ? D.gold : D.dim, fontWeight: i === 0 ? 700 : 400,
              background: i === 0 ? "rgba(200,150,30,0.1)" : "transparent",
              borderLeft: i === 0 ? `2px solid ${D.gold}` : "2px solid transparent",
              marginBottom: 1,
            }}>{item}</div>
          ))}
        </div>

        <div style={{ flex: 1, padding: "14px 16px", overflow: "hidden" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: D.text, marginBottom: 12, fontFamily: F.display }}>
            Dashboard
          </div>

          <div className="mock-stats" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 14 }}>
            {stats.map((s, i) => (
              <div key={i} style={{ background: "rgba(14,29,56,0.8)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "8px 10px" }}>
                <div style={{ fontSize: 8, color: D.dim, marginBottom: 3 }}>{s.label}</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: i % 2 === 0 ? D.gold : D.teal, fontFamily: F.mono }}>{s.val}</div>
                <div style={{ fontSize: 8, color: s.up ? D.em : "#EF4444", marginTop: 2 }}>{s.up || s.down}</div>
              </div>
            ))}
          </div>

          <div style={{ background: "rgba(14,29,56,0.6)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8, padding: "10px 12px", marginBottom: 12, height: 62 }}>
            <div style={{ fontSize: 8, color: D.dim, marginBottom: 8 }}>DELAY RISK BY PROJECT</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 36 }}>
              {[55, 30, 80, 20, 65, 45, 90, 35, 50, 70].map((h, i) => (
                <div key={i} style={{ flex: 1, height: `${h}%`, background: h > 70 ? "rgba(239,68,68,0.7)" : h > 50 ? `rgba(232,184,50,0.7)` : `rgba(16,185,129,0.6)`, borderRadius: "2px 2px 0 0" }} />
              ))}
            </div>
          </div>

          <div style={{ background: "rgba(14,29,56,0.5)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr", padding: "5px 10px", borderBottom: "1px solid rgba(255,255,255,0.05)", background: "rgba(4,12,24,0.5)" }}>
              {["Project", "Health", "Progress"].map(h => (
                <span key={h} style={{ fontSize: 7.5, color: D.dim, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>{h}</span>
              ))}
            </div>
            {rows.map((r, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr", padding: "6px 10px", borderBottom: i < rows.length - 1 ? "1px solid rgba(255,255,255,0.03)" : "none", alignItems: "center" }}>
                <span style={{ fontSize: 9, color: D.text, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
                <span style={{ fontSize: 8, color: r.col, fontWeight: 600 }}>{r.status}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ flex: 1, height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 2 }}>
                    <div style={{ width: `${r.pct}%`, height: "100%", background: r.col, borderRadius: 2 }} />
                  </div>
                  <span style={{ fontSize: 8, color: D.muted, width: 24 }}>{r.pct}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── 3D CUBE (pure CSS) ──────────────────────────────────────── */
function Cube({ size = 64 }) {
  const h = size / 2;
  const faces = [
    `rotateY(0deg) translateZ(${h}px)`,
    `rotateY(90deg) translateZ(${h}px)`,
    `rotateY(180deg) translateZ(${h}px)`,
    `rotateY(-90deg) translateZ(${h}px)`,
    `rotateX(90deg) translateZ(${h}px)`,
    `rotateX(-90deg) translateZ(${h}px)`,
  ];
  const icons = [Building2, Sun, Route, Droplets, Zap, Package];
  return (
    <div style={{ perspective: 600 }}>
      <div className="cube" style={{ width: size, height: size }}>
        {faces.map((t, i) => {
          const I = icons[i];
          return (
            <div key={i} className="cube-face" style={{ transform: t }}>
              <I size={size * 0.42} color={D.gold} strokeWidth={1.6} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── WEB BACKGROUND (animated network across the whole hero) ─────
   Drifting nodes joined by fine lines. The cursor acts as an extra
   node that "connects" to nearby points. Pure canvas.
──────────────────────────────────────────────────────────────── */
function WebBackground() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const LINK = 140, MOUSE_LINK = 170;
    const mouse = { x: -9999, y: -9999 };
    let w = 0, h = 0, raf = 0, visible = true, nodes = [];

    const init = () => {
      const r = canvas.getBoundingClientRect();
      w = r.width; h = r.height;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = Math.max(28, Math.min(110, Math.round((w * h) / 15000)));
      nodes = Array.from({ length: n }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.3 + 1,
        c: Math.random() < 0.35 ? "2,132,199" : "168,112,12",
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      if (!reduce) {
        for (const a of nodes) {
          a.x += a.vx; a.y += a.vy;
          if (a.x < 0 || a.x > w) a.vx *= -1;
          if (a.y < 0 || a.y > h) a.vy *= -1;
        }
      }
      ctx.lineWidth = 0.8;
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < LINK) {
            ctx.strokeStyle = `rgba(${a.c},${(1 - d / LINK) * 0.34})`;
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          }
        }
        const dm = Math.hypot(a.x - mouse.x, a.y - mouse.y);
        if (dm < MOUSE_LINK) {
          ctx.strokeStyle = `rgba(${a.c},${(1 - dm / MOUSE_LINK) * 0.6})`;
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(mouse.x, mouse.y); ctx.stroke();
        }
      }
      for (const a of nodes) {
        ctx.fillStyle = `rgba(${a.c},0.85)`;
        ctx.beginPath(); ctx.arc(a.x, a.y, a.r, 0, Math.PI * 2); ctx.fill();
      }
    };

    const loop = () => {
      if (!visible) { raf = 0; return; }
      draw();
      raf = requestAnimationFrame(loop);
    };

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top;
    };
    const onLeave = () => { mouse.x = -9999; mouse.y = -9999; };

    init();
    if (reduce) draw(); else raf = requestAnimationFrame(loop);

    const ro = new ResizeObserver(() => { init(); if (reduce) draw(); });
    ro.observe(canvas);
    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (visible && !raf && !reduce) raf = requestAnimationFrame(loop);
    });
    io.observe(canvas);
    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseleave", onLeave);

    return () => {
      cancelAnimationFrame(raf); ro.disconnect(); io.disconnect();
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const fade = "linear-gradient(to bottom, #000 72%, transparent 100%)";
  return (
    <canvas ref={ref} aria-hidden="true" style={{
      position: "absolute", inset: 0, width: "100%", height: "100%",
      pointerEvents: "none",
      WebkitMaskImage: fade, maskImage: fade,
    }} />
  );
}

/* ─── HERO ────────────────────────────────────────────────────── */
function Hero() {
  const navigate = useNavigate();
  const [showVideo, setShowVideo] = useState(false);
  const isMobile = useIsMobile();
  const NUMS = [
    { val: "500+", label: "Projects", color: T.gold },
    { val: "6", label: "Sectors", color: T.teal },
    { val: "99.9%", label: "Uptime", color: T.em },
    { val: "3×", label: "Faster Reporting", color: T.gold },
  ];

  const chip = {
    position: "absolute",
    background: "rgba(255,255,255,0.96)",
    border: "1px solid rgba(168,112,12,0.35)",
    borderRadius: 12, padding: "10px 14px",
    boxShadow: "0 18px 36px rgba(15,23,42,0.16), 0 0 0 1px rgba(15,23,42,0.04)",
    display: "flex", alignItems: "center", gap: 10,
    fontSize: 12, color: T.text, fontWeight: 600, whiteSpace: "nowrap",
  };

  return (
    <section style={{ position: "relative", minHeight: "100vh", background: T.bg, paddingTop: 68 }}>
      <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
        {/* everything inside .hero-bg-blur is softly blurred; text & dashboard stay sharp */}
        <div className="hero-bg-blur">
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "linear-gradient(rgba(27,58,142,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(27,58,142,0.08) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }} />
        {/* perspective floor */}
        <div className="floor-grid" />
        {/* animated web across the whole first page */}
        <WebBackground />
        <div className="orb" style={{
          position: "absolute", top: "15%", left: "55%", width: 700, height: 700, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(27,58,142,0.3) 0%, transparent 70%)",
          transform: "translate(-50%,-50%)",
        }} />
        <div className="orb2" style={{
          position: "absolute", top: "60%", left: "75%", width: 500, height: 500, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(200,150,30,0.18) 0%, transparent 70%)",
          transform: "translate(-50%,-50%)",
        }} />
        <div className="orb" style={{
          position: "absolute", top: "30%", left: "20%", width: 400, height: 400, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(0,229,255,0.08) 0%, transparent 70%)",
          transform: "translate(-50%,-50%)",
        }} />
        </div>
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 200, background: `linear-gradient(to top, ${T.bg}, transparent)` }} />
      </div>

      <div className="hero-grid" style={{ position: "relative", maxWidth: 1280, margin: "0 auto", padding: "80px 28px 60px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center", minHeight: "calc(100vh - 68px)" }}>
        {/* Left */}
        <div className="slide-up" style={{ zIndex: 10 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 32,
            background: "rgba(168,112,12,0.08)", border: `1px solid ${T.border}`,
            borderRadius: 999, padding: "6px 16px",
          }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.gold }} />
            <span style={{ fontFamily: F.label, fontSize: 13, letterSpacing: "0.02em", color: T.gold, fontWeight: 600 }}>
              Construction Management ERP
            </span>
          </div>

          <div style={{ position: "relative", marginBottom: 24 }}>
          <h1 style={{
            position: "relative", zIndex: 1,
            fontFamily: F.display, fontWeight: 700, fontSize: "clamp(34px,5vw,62px)", lineHeight: 1.1,
            letterSpacing: "-1.5px", color: T.text, margin: 0,
            textShadow: "none",
          }}>
            Build with{" "}
            <span className="shimmer-txt">precision.</span>
            <br />
            <span style={{ color: T.sky, textShadow: "none" }}>Track</span> every phase.
          </h1>
          </div>

          <p style={{ fontSize: 17, color: T.muted, lineHeight: 1.8, maxWidth: 480, marginBottom: 40 }}>
            Engineering, procurement, site progress and finance — one unified command center for your entire EPC portfolio across 6 construction sectors.
          </p>

          <div className="hero-cta" style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 32 }}>
            <button data-testid="hero-primary-cta"
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-gold-hover"
              style={{
                background: `linear-gradient(135deg, ${T.gold2}, ${T.gold})`,
                color: "#fff", padding: "14px 26px", borderRadius: 10,
                fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 8,
              }}>
              Request Demo <span className="bounce-x" style={{ display: "inline-flex" }}><ArrowRight size={16} /></span>
            </button>
            <button data-testid="hero-secondary-cta"
              onClick={() => navigate("/login")}
              className="btn-glass-hover"
              style={{
                background: "#fff", border: `1.5px solid rgba(15,23,42,0.15)`,
                color: T.text, padding: "14px 26px", borderRadius: 10,
                fontSize: 15, fontWeight: 600, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 8,
              }}>
              Sign In <ChevronRight size={15} />
            </button>
          </div>

          <button
            data-testid="hero-watch-walkthrough-btn"
            onClick={() => setShowVideo(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: 14, background: "none", border: "none", cursor: "pointer", marginBottom: 48, padding: 0 }}
          >
            <div className="play-ring" style={{
              width: 46, height: 46, borderRadius: "50%",
              background: "rgba(168,112,12,0.1)",
              border: "1.5px solid rgba(168,112,12,0.4)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <Play size={16} color={T.gold} fill={T.gold} style={{ marginLeft: 2 }} />
            </div>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Watch 2-min walkthrough</div>
              <div style={{ fontSize: 12, color: T.dim, marginTop: 1 }}>See the full ERP in action</div>
            </div>
          </button>

          <div className="hero-stats" style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
            {NUMS.map((n, i) => (
              <div key={i} style={{ borderLeft: `2px solid ${n.color}`, paddingLeft: 12 }}>
                <div style={{ fontFamily: F.mono, fontSize: 26, fontWeight: 600, color: n.color, lineHeight: 1 }}>{n.val}</div>
                <div style={{ fontSize: 11, color: T.dim, marginTop: 3, fontWeight: 500 }}>{n.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — layered 3D scene */}
        <div style={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center", padding: "30px 0" }}>
          <div style={{
            position: "absolute", top: "50%", left: "50%", width: 480, height: 300, borderRadius: "50%",
            background: "radial-gradient(ellipse, rgba(27,58,142,0.4) 0%, transparent 70%)",
            transform: "translate(-50%,-50%)", filter: "blur(40px)",
          }} />
          <div style={{
            position: "absolute", top: "35%", left: "60%", width: 300, height: 200, borderRadius: "50%",
            background: "radial-gradient(ellipse, rgba(200,150,30,0.2) 0%, transparent 70%)",
            transform: "translate(-50%,-50%)", filter: "blur(30px)",
          }} />

          <div className="hero-float" style={{ width: "100%", maxWidth: 560, position: "relative" }}>
            <Tilt3D max={9} base={isMobile ? [4, -5] : [8, -13]} glare={false} className="no-edge" style={{ width: "100%" }}>
              {/* soft shadow plate behind the dashboard */}
              <div style={{
                position: "absolute", inset: 10, borderRadius: 16,
                background: "rgba(27,58,142,0.35)", border: "1px solid rgba(232,184,50,0.15)",
                transform: "translateZ(-50px)", filter: "blur(1px)",
              }} />
              <div style={{
                position: "absolute", inset: 0, borderRadius: 16,
                background: "rgba(200,150,30,0.12)", border: "1px solid rgba(232,184,50,0.2)",
                transform: "translateZ(-25px)",
              }} />

              <div style={Z(0)}><DashboardMockup /></div>

              {/* floating chips at different depths */}
              <div className="hero-chip hero-chip-a" style={{ ...chip, top: -22, right: -28, animationDelay: "0s" }}>
                <div style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(16,185,129,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Check size={14} color={T.em} strokeWidth={3} />
                </div>
                DPR sent via WhatsApp
              </div>
              <div className="hero-chip hero-chip-b" style={{ ...chip, bottom: 34, left: -40, animationDelay: "1.3s" }}>
                <div style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(168,112,12,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FileText size={14} color={T.gold} />
                </div>
                <span>RA Bill · <span style={{ fontFamily: F.mono, color: T.gold }}>₹42.6L</span></span>
              </div>
              <div className="hero-chip hero-chip-c" style={{ ...chip, bottom: -24, right: 30, animationDelay: "2.2s" }}>
                <div style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(8,145,178,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <TrendingUp size={14} color={T.teal} />
                </div>
                Delay risk ↓ 18%
              </div>

              {/* spinning sector cube */}
              <div className="hero-cube" style={{ position: "absolute", top: 40, left: -70, transform: "translateZ(90px)" }}>
                <Cube size={56} />
              </div>
            </Tilt3D>
          </div>
        </div>
      </div>

      <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ position: "absolute", bottom: -1, left: 0, right: 0, width: "100%", height: 60 }}>
        <path d="M0,0 C360,60 1080,60 1440,0 L1440,60 L0,60 Z" fill={T.bg2} />
      </svg>

      {showVideo && <VideoModal onClose={() => setShowVideo(false)} />}
    </section>
  );
}

/* ─── SECTOR TICKER (slow scrolling strip under the hero) ─────── */
const TICKER = [
  { icon: Route, l: "Highways & Infrastructure" },
  { icon: Building2, l: "High-Rise Commercial" },
  { icon: Sun, l: "Solar & Renewables" },
  { icon: Droplets, l: "Water Infrastructure" },
  { icon: Zap, l: "Power Transmission" },
  { icon: Package, l: "Industrial EPC" },
];
function SectorTicker() {
  const items = [...TICKER, ...TICKER];
  const edge = "linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)";
  return (
    <div aria-hidden="true" style={{ background: T.bg2, padding: "4px 0 30px" }}>
      <div className="ticker" style={{ overflow: "hidden", WebkitMaskImage: edge, maskImage: edge }}>
        <div className="ticker-track">
          {items.map((it, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, color: T.dim, fontSize: 13, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
              <it.icon size={16} color={T.gold} strokeWidth={1.7} />
              {it.l}
              <span style={{ margin: "0 30px", width: 4, height: 4, borderRadius: "50%", background: "rgba(168,112,12,0.5)" }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── FEATURES ────────────────────────────────────────────────── */
const FEATURES = [
  { icon: LayoutDashboard, tag: "Portfolio Intelligence", title: "Portfolio Command Center", size: "lg", id: "dashboard",
    desc: "A real-time executive view of your entire EPC portfolio — project health, physical progress, cost position and delay risk — so leadership can act early, not after the fact.",
    points: ["Live project KPIs", "Early delay-risk alerts", "Multi-project, multi-sector view"] },
  { icon: ClipboardCheck, tag: "Field Execution", title: "Site Operations & Daily Reporting", size: "sm", id: "fieldops",
    desc: "Capture daily progress, manpower and material updates from site on mobile and share them with clients and management instantly.",
    points: ["Mobile-first DPR capture", "WhatsApp & email dispatch"] },
  { icon: Users, tag: "Client Relations", title: "Client & Account Management", size: "sm", id: "clients",
    desc: "One structured record for every client — contacts, active projects, billing to date and account status — for faster follow-ups.",
    points: ["Contact & project directory", "Billing and status at a glance"] },
  { icon: IndianRupee, tag: "Finance & Accounts", title: "Billing, Accounts & Finance", size: "sm", id: "finance",
    desc: "Raise RA bills, post journals, maintain the general ledger and close periods with confidence, with seamless Tally export.",
    points: ["RA billing & general ledger", "Period closing & Tally export"] },
  { icon: Calculator, tag: "Cost Planning", title: "Estimation & BOQ Management", size: "lg", id: "estimates",
    desc: "Prepare accurate estimates and Bills of Quantities from sector-specific templates, then carry approved figures straight into project budgets and cost tracking — with no re-entry and no gaps between planning and execution.",
    points: ["Sector-specific templates", "Estimate-to-budget handover", "Planned vs actual variance"] },
  { icon: Truck, tag: "Procurement", title: "Procurement & Vendor Management", size: "sm", id: "vendors",
    desc: "Run the full procure-to-pay cycle — indents, RFQs, purchase orders, GRNs and vendor bills — with a clear audit trail.",
    points: ["Indent → RFQ → PO → GRN", "Transparent vendor trail"] },
];

function FeatureCard({ f, i = 0 }) {
  const lg = f.size === "lg";
  return (
    <Tilt3D
      data-testid="feature-card-item"
      className={`reveal ${lg ? "feature-lg" : ""}`}
      max={7}
      style={{
        background: CARD_BG,
        border: `1px solid ${T.border}`,
        borderRadius: 20, padding: lg ? "36px 34px 32px" : "30px 26px 28px",
        gridColumn: lg ? "span 2" : "span 1",
        display: "flex", flexDirection: "column",
        cursor: "default", "--d": `${i * 80}ms`,
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 14,
          background: "linear-gradient(135deg, rgba(42,82,190,0.12), rgba(168,112,12,0.18))",
          border: "1px solid rgba(168,112,12,0.3)",
          boxShadow: "0 10px 20px rgba(15,23,42,0.16), inset 0 1px 0 rgba(255,255,255,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center",
          ...Z(45),
        }}>
          <f.icon size={24} color={T.gold} strokeWidth={1.8} />
        </div>
        <span style={{ fontFamily: F.mono, fontSize: 30, fontWeight: 700, letterSpacing: "-1px", lineHeight: 1, color: "rgba(15,23,42,0.08)", ...Z(18) }}>
          {String(i + 1).padStart(2, "0")}
        </span>
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: T.gold, marginBottom: 10, ...Z(22) }}>{f.tag}</div>
      <h3 style={{ fontFamily: F.display, fontSize: lg ? 24 : 19, fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.3px", color: T.text, marginBottom: 12, ...Z(28) }}>{f.title}</h3>
      <p style={{ color: T.muted, fontSize: 14.5, lineHeight: 1.75, marginBottom: 24, ...Z(12) }}>{f.desc}</p>

      <div style={{
        marginTop: "auto", paddingTop: 18, borderTop: `1px solid ${T.border}`,
        display: "grid", gridTemplateColumns: lg ? "repeat(auto-fit, minmax(170px, 1fr))" : "1fr", gap: "10px 18px",
        ...Z(16),
      }}>
        {f.points.map((pt) => (
          <div key={pt} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 18, height: 18, borderRadius: "50%", background: "rgba(16,185,129,0.12)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Check size={11} color={T.em} strokeWidth={3} />
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{pt}</span>
          </div>
        ))}
      </div>
    </Tilt3D>
  );
}

function Features() {
  return (
    <section id="features" className="sec glow-a" style={{ background: T.bg2, padding: "110px 28px 110px", position: "relative" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <SectionLabel>Platform Capabilities</SectionLabel>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 60, flexWrap: "wrap", gap: 20 }}>
          <h2 className="reveal" style={{ fontFamily: F.display, fontWeight: 700, fontSize: "clamp(28px,4vw,48px)", color: T.text, letterSpacing: "-0.5px", maxWidth: 640, lineHeight: 1.12 }}>
            One platform for every stage of{" "}
            <span className="gold-grad">project delivery.</span>
          </h2>
          <p style={{ color: T.muted, fontSize: 15.5, maxWidth: 420, lineHeight: 1.8 }}>
            Six integrated modules connecting estimation, site execution, procurement and finance — replacing spreadsheets and disconnected tools with a single source of truth.
          </p>
        </div>

        <div data-testid="features-bento-grid" className="features-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 24 }}>
          {FEATURES.map((f, i) => <FeatureCard key={f.id} f={f} i={i} />)}
        </div>
      </div>

      <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ position: "absolute", bottom: -1, left: 0, right: 0, width: "100%", height: 60 }}>
        <path d="M0,60 C480,0 960,0 1440,60 L1440,60 L0,60 Z" fill={T.bg} />
      </svg>
    </section>
  );
}

/* ─── SECTORS ─────────────────────────────────────────────────── */
const SECTORS = [
  { id: "roads", label: "Infrastructure & Highways", icon: Route, color: T.em,
    desc: "Chainage-based WBS for highways and expressways. Track earthwork volumes, quality tests and highway authority reporting.", stats: ["Chainage-based WBS", "Earthwork volumes", "QC test records"], img: "https://images.unsplash.com/photo-1526546334624-2afe5b01088d?w=600&q=75" },
  { id: "buildings", label: "High-Rise Commercial", icon: Building2, color: "#60A5FA",
    desc: "Floor-wise tracking, BOQ management and RA billing to developers for high-rise and commercial building projects.", stats: ["Floor-wise schedule", "BOQ variance", "RA bill generation"], img: "https://images.unsplash.com/photo-1727206042514-75b187a8221e?w=600&q=75" },
  { id: "solar", label: "Solar & Renewable Power", icon: Sun, color: T.gold,
    desc: "MWp capacity tracking, inverter-level DPR and per-MWp cost variance for utility-scale and rooftop solar projects.", stats: ["MWp capacity tracking", "Inverter-level DPR", "SCADA link ready"], img: "https://images.unsplash.com/photo-1515489472349-73b65ec1247c?w=600&q=75" },
  { id: "water", label: "Water Infrastructure", icon: Droplets, color: "#38BDF8",
    desc: "km-based progress tracking, pipe inventory management and pump commissioning for water treatment and pipeline projects.", stats: ["km pipeline tracking", "Pipe inventory", "Test cert records"] },
  { id: "power", label: "Power Transmission", icon: Zap, color: "#A78BFA",
    desc: "Tower-wise WBS, stringing progress and MVA commissioning for EHV transmission lines and substations.", stats: ["Tower-wise WBS", "Stringing progress", "MVA commissioning"] },
  { id: "industrial", label: "Industrial EPC", icon: Package, color: "#FB923C",
    desc: "Equipment procurement, civil & structural milestones and utilities commissioning for factories and industrial plants.", stats: ["Equipment log", "Civil milestones", "Commissioning list"] },
];

function hexToRgb(hex) {
  try { const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16); return `${r},${g},${b}`; }
  catch { return "255,255,255"; }
}

function Sectors() {
  const [active, setActive] = useState("roads");
  const sec = SECTORS.find(s => s.id === active) || SECTORS[0];
  const rgb = hexToRgb(sec.color);
  return (
    <section id="sectors" className="sec glow-c" style={{ background: T.bg, padding: "100px 28px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <SectionLabel>Construction Sectors</SectionLabel>
        <h2 className="reveal" style={{ fontFamily: F.display, fontWeight: 700, fontSize: "clamp(28px,4vw,48px)", color: T.text, letterSpacing: "-0.5px", marginBottom: 48, lineHeight: 1.12 }}>
          Built for every type of <span className="gold-grad">EPC project.</span>
        </h2>

        <div className="sector-tabs" style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 36 }}>
          {SECTORS.map(s => (
            <button key={s.id} data-testid="sector-tab-button"
              onClick={() => setActive(s.id)}
              className={active === s.id ? "sector-tab-active" : "btn-glass-hover"}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "9px 18px", borderRadius: 10,
                border: `1.5px solid ${active === s.id ? "rgba(168,112,12,0.5)" : T.borderB}`,
                background: active === s.id ? "rgba(27,58,142,0.25)" : "#fff",
                color: active === s.id ? T.gold : T.muted,
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                fontFamily: F.body, transition: "all 0.2s",
              }}>
              <s.icon size={14} />
              {s.label}
            </button>
          ))}
        </div>

        {/* Active sector card — 3D tilt, image panel pops forward */}
        <Tilt3D max={4} data-testid="sector-content-card" className="reveal sector-card" style={{
          display: "grid", gridTemplateColumns: "1fr 1fr",
          gap: 32, background: CARD_BG,
          border: `1px solid rgba(${rgb},0.25)`,
          borderRadius: 20,
          boxShadow: `0 0 60px rgba(${rgb},0.08)`,
        }}>
          <div className="sector-pad" style={{ padding: "40px 40px" }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14, marginBottom: 24,
              background: `rgba(${rgb},0.12)`,
              border: `1px solid rgba(${rgb},0.25)`,
              boxShadow: "0 10px 20px rgba(15,23,42,0.12)",
              display: "flex", alignItems: "center", justifyContent: "center",
              ...Z(40),
            }}>
              <sec.icon size={28} color={sec.color} strokeWidth={1.8} />
            </div>
            <h3 style={{ fontFamily: F.display, fontSize: 27, fontWeight: 700, color: T.text, marginBottom: 14, ...Z(28) }}>{sec.label}</h3>
            <p style={{ color: T.muted, fontSize: 15, lineHeight: 1.85, marginBottom: 28, ...Z(14) }}>{sec.desc}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, ...Z(20) }}>
              {sec.stats.map((st, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 20, height: 20, borderRadius: "50%", background: `rgba(${rgb},0.12)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Check size={11} color={sec.color} strokeWidth={2.5} />
                  </div>
                  <span style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>{st}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="sector-img-wrap" style={{ padding: 20, display: "flex" }}>
            <div style={{
              flex: 1, position: "relative", minHeight: 280, borderRadius: 16, overflow: "hidden",
              background: sec.img
                ? `url(${sec.img}) center/cover`
                : `linear-gradient(135deg, rgba(${rgb},0.08), rgba(255,255,255,0.8))`,
              boxShadow: `0 24px 44px rgba(15,23,42,0.18), 0 0 40px rgba(${rgb},0.15)`,
              border: `1px solid rgba(${rgb},0.3)`,
              ...Z(50),
            }}>
              {sec.img && <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, rgba(15,23,42,0.18), transparent)" }} />}
              {!sec.img && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <sec.icon size={120} color={`rgba(${rgb},0.12)`} strokeWidth={0.5} />
                </div>
              )}
            </div>
          </div>
        </Tilt3D>
      </div>
    </section>
  );
}

/* ─── HOW IT WORKS ────────────────────────────────────────────── */
const STEPS = [
  { num: "01", icon: ClipboardList, title: "Connect Sites & WBS", desc: "Import your estimate master, apply a sector-specific WBS template, and sync BOQs across all project sites in minutes." },
  { num: "02", icon: TrendingUp, title: "Real-time Field DPR", desc: "Site engineers log progress on mobile. Actual cost and schedule variance updates instantly — no manual re-entry." },
  { num: "03", icon: Shield, title: "Automated Bills & Margin Protection", desc: "Generate RA bills for clients, catch cost overruns early with AI alerts, and send DPRs directly via WhatsApp." },
];

function HowItWorks() {
  return (
    <section id="how-it-works" className="sec glow-b" style={{ background: T.bg2, padding: "100px 28px", position: "relative" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <SectionLabel>How It Works</SectionLabel>
        <h2 className="reveal" style={{ fontFamily: F.display, fontWeight: 700, fontSize: "clamp(28px,4vw,48px)", color: T.text, letterSpacing: "-0.5px", marginBottom: 64, lineHeight: 1.12 }}>
          From site to boardroom, <span className="gold-grad">in three steps.</span>
        </h2>

        <div className="steps-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24, position: "relative" }}>
          <div className="steps-line" style={{ position: "absolute", top: 48, left: "16%", right: "16%", height: 1, background: `linear-gradient(to right, transparent, ${T.gold}, ${T.gold}, transparent)`, opacity: 0.2 }} />

          {STEPS.map((s, i) => (
            <Tilt3D key={i} max={10} className="reveal" style={{ "--d": `${i * 120}ms`,
              background: CARD_BG,
              border: `1px solid ${T.border}`, borderRadius: 18, padding: "36px 28px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
                <div style={{
                  width: 52, height: 52, borderRadius: 14,
                  background: `linear-gradient(135deg, rgba(42,82,190,0.12), rgba(168,112,12,0.18))`,
                  border: `1px solid rgba(168,112,12,0.25)`,
                  boxShadow: "0 12px 22px rgba(15,23,42,0.16), inset 0 1px 0 rgba(255,255,255,0.6)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, ...Z(55),
                }}>
                  <s.icon size={24} color={T.gold} strokeWidth={1.8} />
                </div>
                <div style={{ fontFamily: F.mono, fontSize: 48, fontWeight: 600, color: "rgba(168,112,12,0.22)", lineHeight: 1, letterSpacing: "-2px", ...Z(20) }}>{s.num}</div>
              </div>
              <h3 style={{ fontFamily: F.display, fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 12, ...Z(30) }}>{s.title}</h3>
              <p style={{ color: T.muted, fontSize: 14, lineHeight: 1.8, ...Z(12) }}>{s.desc}</p>
            </Tilt3D>
          ))}
        </div>
      </div>
      <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ position: "absolute", bottom: -1, left: 0, right: 0, width: "100%", height: 60 }}>
        <path d="M0,0 C360,60 1080,60 1440,0 L1440,60 L0,60 Z" fill={T.bg} />
      </svg>
    </section>
  );
}

/* ─── PRICING ─────────────────────────────────────────────────── */
const PLANS = [
  {
    name: "Growth", price: { mo: 4999, yr: 3999 },
    desc: "For small contractors managing 1–5 active projects.",
    features: ["Up to 5 projects", "3 user accounts", "WBS & Daily Reports", "Basic Procurement", "Email support"],
    highlighted: false,
  },
  {
    name: "Professional", price: { mo: 10999, yr: 11999 },
    desc: "For growing EPC firms with multi-sector portfolios.",
    features: ["Up to 25 projects", "20 user accounts", "Full ERP (Finance, GL, Stores)", "AI DPR extraction", "Role-based permissions", "WhatsApp / Email alerts", "Priority support"],
    highlighted: true,
  },
  {
    name: "Enterprise", price: null,
    desc: "Unlimited scale with dedicated onboarding & integrations.",
    features: ["Unlimited projects & users", "Custom WBS templates", "Tally / SAP integration", "Dedicated account manager", "On-premise option", "SLA-backed uptime"],
    highlighted: false,
  },
];

function Pricing() {
  const [annual, setAnnual] = useState(false);
  const navigate = useNavigate();
  return (
    <section id="pricing" className="sec glow-a" style={{ background: T.bg, padding: "100px 28px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <SectionLabel>Transparent Pricing</SectionLabel>
        <h2 className="reveal" style={{ fontFamily: F.display, fontWeight: 700, fontSize: "clamp(28px,4vw,48px)", color: T.text, letterSpacing: "-0.5px", marginBottom: 14, lineHeight: 1.12 }}>
          Simple pricing, <span className="gold-grad">no surprises.</span>
        </h2>
        <p style={{ color: T.muted, fontSize: 15, marginBottom: 40 }}>All plans include a 14-day free trial. No credit card required.</p>

        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 52 }}>
          <span style={{ fontSize: 14, color: annual ? T.dim : T.text, fontWeight: 500 }}>Monthly</span>
          <button data-testid="pricing-billing-toggle" onClick={() => setAnnual(!annual)} style={{
            width: 50, height: 28, borderRadius: 14, background: annual ? `linear-gradient(135deg,${T.gold2},${T.gold})` : "rgba(15,23,42,0.18)",
            border: "none", cursor: "pointer", position: "relative", transition: "background .3s",
            boxShadow: "inset 0 2px 5px rgba(15,23,42,0.2)",
          }}>
            <span style={{ position: "absolute", top: 4, left: annual ? 26 : 4, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left .25s", boxShadow: "0 2px 5px rgba(15,23,42,0.35)" }} />
          </button>
          <span style={{ fontSize: 14, color: annual ? T.text : T.dim, fontWeight: 500 }}>Annual</span>
          {annual && <span style={{ background: "rgba(16,185,129,0.12)", color: T.em, border: "1px solid rgba(16,185,129,0.25)", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 6, letterSpacing: "0.05em" }}>SAVE 20%</span>}
        </div>

        <div className="pricing-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24, alignItems: "stretch" }}>
          {PLANS.map((p, i) => (
            <Tilt3D key={i} data-testid="pricing-plan-card" className="reveal pricing-card" max={p.highlighted ? 7 : 9} style={{
              background: p.highlighted
                ? `linear-gradient(145deg, #FFF7E3, #FFFFFF)`
                : CARD_BG,
              border: p.highlighted ? `1.5px solid rgba(168,112,12,0.45)` : `1px solid ${T.border}`,
              borderRadius: 20, padding: "36px 28px", "--d": `${i * 120}ms`,
              boxShadow: p.highlighted ? `0 0 50px rgba(168,112,12,0.12), 0 24px 50px rgba(15,23,42,0.14)` : undefined,
              marginTop: p.highlighted ? -12 : 0, marginBottom: p.highlighted ? -12 : 0,
            }}>
              {p.highlighted && (
                <div style={{
                  position: "absolute", top: -12, left: "50%", marginLeft: -50,
                  background: `linear-gradient(135deg,${T.gold2},${T.gold})`, color: "#fff",
                  fontSize: 11, fontWeight: 700, padding: "4px 16px", borderRadius: 999,
                  whiteSpace: "nowrap", letterSpacing: "0.08em", textTransform: "uppercase",
                  boxShadow: "0 4px 0 #6B4807, 0 10px 20px rgba(15,23,42,0.18)",
                  ...Z(60),
                }}>Most Popular</div>
              )}
              <h3 style={{ fontFamily: F.display, fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 8, ...Z(25) }}>{p.name}</h3>
              <p style={{ fontSize: 13, color: T.dim, marginBottom: 28, lineHeight: 1.6 }}>{p.desc}</p>
              {p.price ? (
                <div style={{ marginBottom: 28, ...Z(45) }}>
                  <span style={{ fontFamily: F.mono, fontSize: 38, fontWeight: 600, color: p.highlighted ? T.gold : T.text }}>
                    ₹{(annual ? p.price.yr : p.price.mo).toLocaleString("en-IN")}
                  </span>
                  <span style={{ color: T.dim, fontSize: 14 }}>/mo</span>
                </div>
              ) : (
                <div style={{ marginBottom: 28, ...Z(45) }}>
                  <span style={{ fontFamily: F.display, fontSize: 36, fontWeight: 700, color: T.text }}>Custom</span>
                </div>
              )}
              <div style={{ marginBottom: 28, display: "flex", flexDirection: "column", gap: 11 }}>
                {p.features.map((f, j) => (
                  <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", background: p.highlighted ? "rgba(168,112,12,0.12)" : "rgba(16,185,129,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                      <Check size={11} color={p.highlighted ? T.gold : T.em} strokeWidth={2.5} />
                    </div>
                    <span style={{ fontSize: 13, color: "#334155" }}>{f}</span>
                  </div>
                ))}
              </div>
              <button data-testid="pricing-plan-cta"
                onClick={() => p.name === "Enterprise" ? document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }) : navigate("/login")}
                className={p.highlighted ? "btn-gold-hover" : "btn-glass-hover"}
                style={{
                  width: "100%",
                  background: p.highlighted ? `linear-gradient(135deg,${T.gold2},${T.gold})` : "transparent",
                  color: p.highlighted ? "#fff" : T.text,
                  border: p.highlighted ? "none" : `1.5px solid ${T.borderB}`,
                  borderRadius: 10, padding: 14, fontSize: 14,
                  fontWeight: 700, cursor: "pointer", fontFamily: F.body,
                }}>
                {p.name === "Enterprise" ? "Contact Sales" : "Start Free Trial"}
              </button>
            </Tilt3D>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── TESTIMONIALS ────────────────────────────────────────────── */
const TESTIMONIALS = [
  { name: "Rajesh Menon", role: "Director – EPC Operations", company: "NovaBuild Infrastructure", text: "Sitera replaced three disconnected tools overnight. The DPR-to-WhatsApp automation alone saves my team 2 hours daily. Our clients love the transparency.", stars: 5 },
  { name: "Priya Subramaniam", role: "CFO", company: "Greenfield Highways Ltd.", text: "Month-end reconciliation that used to take three days now takes thirty minutes. The Tally export is flawless. Zero data-entry errors since we switched.", stars: 5 },
  { name: "Arjun Tiwari", role: "Head of Projects", company: "Apex Solar EPC", text: "We manage 18 solar projects across four states. Sitera's portfolio view and per-MWp cost drill-down is exactly what we needed. Deployment was same-day.", stars: 5 },
];

function Testimonials() {
  return (
    <section className="sec glow-c" style={{ background: T.bg2, padding: "100px 28px", position: "relative" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <SectionLabel>Client Stories</SectionLabel>
        <h2 className="reveal" style={{ fontFamily: F.display, fontWeight: 700, fontSize: "clamp(28px,4vw,48px)", color: T.text, letterSpacing: "-0.5px", marginBottom: 56, lineHeight: 1.12 }}>
          Trusted by EPC teams <span className="gold-grad">across India.</span>
        </h2>
        <div className="testi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24 }}>
          {TESTIMONIALS.map((t, i) => (
            <Tilt3D key={i} max={8} className="reveal" style={{ "--d": `${i * 120}ms`,
              background: CARD_BG,
              border: `1px solid ${T.border}`, borderRadius: 16, padding: "32px",
            }}>
              <div style={{ display: "flex", gap: 3, marginBottom: 20, ...Z(30) }}>
                {[...Array(t.stars)].map((_, j) => <Star key={j} size={14} color={T.gold} fill={T.gold} />)}
              </div>
              <Quote size={22} color="rgba(168,112,12,0.25)" style={{ marginBottom: 14, ...Z(40) }} />
              <p style={{ fontSize: 14, color: "#334155", lineHeight: 1.85, marginBottom: 28, ...Z(12) }}>{t.text}</p>
              <div style={{ display: "flex", alignItems: "center", gap: 14, borderTop: `1px solid ${T.border}`, paddingTop: 20 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: "50%",
                  background: `linear-gradient(135deg, ${T.blue2}, ${T.blue})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: F.display, fontWeight: 700, fontSize: 16, color: "#fff",
                  boxShadow: "0 8px 16px rgba(15,23,42,0.18)",
                  ...Z(45),
                }}>
                  {t.name.charAt(0)}
                </div>
                <div style={Z(20)}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: T.dim }}>{t.role} · {t.company}</div>
                </div>
              </div>
            </Tilt3D>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CONTACT ─────────────────────────────────────────────────── */
function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const onSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) { toast.error("Please fill Name, Company and Email."); return; }
    setBusy(true);
    await new Promise(r => setTimeout(r, 900));
    setBusy(false);
    toast.success("Demo request received! We'll reach you within 24 hours.");
    setForm({ name: "", company: "", email: "", phone: "", message: "" });
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box",
    background: "#fff", border: `1px solid ${T.borderB}`,
    borderRadius: 9, padding: "11px 14px", color: T.text, fontSize: 14, outline: "none",
    fontFamily: F.body, transition: "border-color 0.2s, box-shadow 0.2s",
    boxShadow: "inset 0 1px 3px rgba(15,23,42,0.10)",
  };
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.12em" };

  const inp = (id, label, ph, type = "text") => (
    <div>
      <label style={labelStyle}>{label}</label>
      <input data-testid={`contact-${id}-input`} type={type} value={form[id]} placeholder={ph}
        onChange={e => setForm(p => ({ ...p, [id]: e.target.value }))}
        style={inputStyle}
        onFocus={e => e.target.style.borderColor = T.gold}
        onBlur={e => e.target.style.borderColor = T.borderB}
      />
    </div>
  );

  return (
    <section id="contact" className="sec glow-b" style={{ background: T.bg, padding: "100px 28px" }}>
      <div className="contact-grid" style={{ maxWidth: 1160, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1.25fr", gap: 72, alignItems: "start" }}>
        <div>
          <SectionLabel>Get In Touch</SectionLabel>
          <h2 className="reveal" style={{ fontFamily: F.display, fontWeight: 700, fontSize: "clamp(28px,3.5vw,44px)", color: T.text, letterSpacing: "-0.5px", marginBottom: 18, lineHeight: 1.12 }}>
            Ready to see <span className="gold-grad">Sitera in action?</span>
          </h2>
          <p style={{ color: T.muted, fontSize: 15, lineHeight: 1.85, marginBottom: 48 }}>
            Book a 30-minute live demo with our EPC product team. We'll show you exactly how Sitera works for your sector and project scale.
          </p>
          {[
            { icon: Mail, txt: "sales@sparkcurv.com" },
            { icon: Phone, txt: "+91 96774 95028" },
            { icon: MapPin, title: "Sparkcurv Technologies Pvt. Ltd.", txt: "64/3, Thompson St, Palace Rd, Nagercoil, Tamil Nadu 629001" },
          ].map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 11, flexShrink: 0,
                background: "rgba(168,112,12,0.08)", border: `1px solid ${T.border}`,
                boxShadow: "0 3px 0 rgba(168,112,12,0.2), 0 8px 16px rgba(15,23,42,0.08)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <item.icon size={18} color={T.gold} />
              </div>
              <div>
                {item.title && <div style={{ fontWeight: 700, color: T.text, fontSize: 14, marginBottom: 3 }}>{item.title}</div>}
                <span style={{ color: "#334155", fontSize: 14, lineHeight: 1.6 }}>{item.txt}</span>
              </div>
            </div>
          ))}
        </div>

        <Tilt3D max={3} glare={false} className="reveal" style={{
          background: CARD_BG,
          border: `1px solid ${T.border}`, borderRadius: 20,
          boxShadow: `0 0 60px rgba(27,58,142,0.12)`,
        }}>
          <form onSubmit={onSubmit} data-testid="contact-demo-form" className="form-pad" style={{
            padding: "40px", display: "flex", flexDirection: "column", gap: 20,
          }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
              {inp("name", "Full Name", "Your name")}
              {inp("company", "Company", "Your company")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
              {inp("email", "Email", "you@company.com", "email")}
              {inp("phone", "Phone", "+91 …", "tel")}
            </div>
            <div>
              <label style={labelStyle}>Message</label>
              <textarea data-testid="contact-message-input" value={form.message} rows={4} placeholder="Tell us about your projects and team size..."
                onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                style={{ ...inputStyle, resize: "vertical" }}
                onFocus={e => e.target.style.borderColor = T.gold}
                onBlur={e => e.target.style.borderColor = T.borderB}
              />
            </div>
            <button data-testid="contact-submit-button" type="submit" disabled={busy} className={busy ? "" : "btn-gold-hover"}
              style={{
                background: busy ? T.dim : `linear-gradient(135deg,${T.gold2},${T.gold})`,
                color: "#fff", border: "none", borderRadius: 10, padding: 14,
                fontSize: 15, fontWeight: 700, cursor: busy ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                fontFamily: F.body,
              }}>
              {busy ? "Sending…" : <><span>Request a Demo</span> <ArrowRight size={16} /></>}
            </button>
          </form>
        </Tilt3D>
      </div>
    </section>
  );
}

/* ─── FOOTER ──────────────────────────────────────────────────── */
function Footer() {
  const navigate = useNavigate();
  return (
    <footer className="footer-root" style={{ background: "#040C18", borderTop: "1px solid rgba(255,255,255,0.08)", padding: "56px 28px 32px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div className="footer-grid" style={{ display: "grid", gridTemplateColumns: "2.5fr 1fr 1fr 1fr", gap: 48, marginBottom: 48 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <img src="/brand/sitera-logo.png" alt="Sitera" style={{ height: 34, width: 34, objectFit: "contain" }} />
              <span style={{ fontFamily: F.display, fontWeight: 700, fontSize: 21, color: D.text }}>
                SITE<span style={{ color: D.gold }}>RA</span>
              </span>
            </div>
            <p style={{ fontSize: 13, color: D.dim, lineHeight: 1.8, maxWidth: 280 }}>
              The construction ERP engineered for EPC contractors — from blueprint to delivery across 6 sectors.
            </p>
          </div>
          {[
            { title: "Product", links: [["Features", "#features"], ["Sectors", "#sectors"], ["Pricing", "#pricing"], ["Changelog", "#features"]] },
            { title: "Company", links: [["About", "#top"], ["Careers", "#contact"], ["Blog", "#top"], ["Press", "#contact"]] },
            { title: "Legal", links: [["Privacy", "#top"], ["Terms", "#top"], ["Security", "#top"], ["Cookies", "#top"]] },
          ].map((col, i) => (
            <div key={i}>
              <h4 style={{ fontFamily: F.display, fontSize: 12, fontWeight: 700, color: D.text, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 18 }}>{col.title}</h4>
              {col.links.map(([label, href]) => (
                <div key={label} style={{ marginBottom: 11 }}>
                  <a href={href} style={{ fontSize: 13, color: D.dim, textDecoration: "none", transition: "color 0.2s" }}
                    onMouseEnter={e => e.target.style.color = "#CBD5E1"}
                    onMouseLeave={e => e.target.style.color = D.dim}>{label}</a>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <span style={{ fontSize: 12, color: D.dim }}>© 2026 Sitera · A <span style={{ color: D.gold }}>Sparkcurv</span> Product. All rights reserved.</span>
          <button onClick={() => navigate("/login")}
            style={{ background: "none", border: "none", color: D.gold, fontSize: 13, fontWeight: 600, cursor: "pointer", textDecoration: "underline", fontFamily: F.body }}>
            Sign In →
          </button>
        </div>
      </div>
    </footer>
  );
}

/* ─── SHARED LABEL ────────────────────────────────────────────── */
function SectionLabel({ children }) {
  return (
    <div className="reveal" style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
      <span style={{ width: 20, height: 2, background: T.gold, display: "inline-block", borderRadius: 2 }} />
      <span style={{ fontFamily: F.mono, fontSize: 11, letterSpacing: "0.12em", color: T.gold, fontWeight: 600, textTransform: "uppercase" }}>{children}</span>
    </div>
  );
}

/* ─── ROOT ────────────────────────────────────────────────────── */
export default function LandingPage() {
  useReveal();

  return (
    <>
      <style>{STYLES}</style>
      <div id="top" style={{ background: T.bg, minHeight: "100vh", fontFamily: F.body, overflowX: "hidden" }}>
        <Header />
        <Hero />
        <SectorTicker />
        <Features />
        <Sectors />
        <HowItWorks />
        <Pricing />
        <Testimonials />
        <Contact />
        <Footer />
      </div>
    </>
  );
}